import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";

import * as firebase from "firebase/app";
import "firebase/firestore";
import { environment } from "../../environments/environment";
import Exercise, { ExerciseQuiz, ExerciseUser } from "../models/exercise.model";
import { HttpClient } from "@angular/common/http";
import * as _ from "lodash";
import { AngularFireFunctions } from "@angular/fire/functions";

@Injectable({
  providedIn: "root",
})
export class ExerciseService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  functionsUrl = environment.functionsUrl;
  userLogin: User;
  private dbPath = "/exercises";
  contentRef: AngularFirestoreCollection<Exercise>;

  constructor(
    private http: HttpClient,
    private fns: AngularFireFunctions,
    private db: AngularFirestore,
    private userStore: UserService
  ) {
    this.contentRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  getTopPublishedExercises(
    timestamp?: number
  ): AngularFirestoreCollection<Exercise> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        .where("status", "==", 1)
        // .where("timestamp", ">=", timestamp)
        // .orderBy("timestamp", "desc")
        //.orderBy("noLiked", "desc")
        .orderBy("noExUserCompleted", "desc")
        .limit(10)
    );
  }

  getPublishedExercises(limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("privacy", "==", false)
      .where("status", "==", 1)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getPublishedCourseExercises(courseId, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("status", "==", 1)
      .where("courseId", "==", courseId)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getPublishedByAuthor(uid: string): AngularFirestoreCollection<Exercise> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        .where("status", "==", 1)
        .where("createdBy", "==", uid)
        .orderBy("createdOn", "desc")
        .limit(5)
    );
  }

  getExercisesSameTag(tag: string): AngularFirestoreCollection<Exercise> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("privacy", "==", false)
        .where("status", "==", 1)
        .where("tags", "array-contains", tag)
        .orderBy("createdOn", "desc")
        .limit(2)
    );
  }

  getMyExercises(uid: string, limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("createdBy", "==", uid)
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  getCourseExercises(courseId: string): AngularFirestoreCollection<Exercise> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("courseId", "==", courseId)
        .orderBy("createdOn", "desc")
    );
  }

  get(id: string) {
    return this.contentRef.doc(id).valueChanges();
  }

  getById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  create(content: Exercise): any {
    content.isRepeated = content.isRepeated ? content.isRepeated : true;
    content.percentCompleted = content.percentCompleted
      ? content.percentCompleted
      : 0;
    content.duration = content.duration ? content.duration : 0;
    content.tags = content.tags ? content.tags : [];
    content.privacy = content.privacy ? content.privacy : false;
    content.status = content.status ? content.status : 0;
    content.exQuizzes = content.exQuizzes ? content.exQuizzes : [];
    content.exerciseUsers = [];

    content.noExUser = 0;
    content.noExUserCompleted = 0;

    content.noQuiz = 0;
    content.noLiked = 0;
    content.noComment = 0;
    content.noViewer = 0;

    //Moderator
    content.isReviewed = false;
    content.isApproved =
      this.userLogin.admin || this.userLogin.moderator || this.userLogin.tutor
        ? true
        : false;
    content.approvedOn = new Date();
    content.approvedBy = this.userLogin.uid;
    content.approvedAuthor = this.userLogin;

    content.isActive = true;
    content.isDeleted = false;
    content.createdOn = new Date();
    content.createdBy = this.userLogin.uid;
    content.updatedOn = new Date();
    content.updatedBy = this.userLogin.uid;
    content.author = this.userLogin;
    content.timestamp = new Date().getTime();
    content.visibility = this.visibility;

    return this.contentRef.add({ ...content }).then((docRef) => {
      content.id = docRef.id;
      const syncData = { id: content.id, ...content };
      this.sync(syncData);

      return content;
    });
  }

  update(content: Exercise): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    console.log("ExerciseService/update:", content);
    return this.contentRef
      .doc(content.id)
      .update({
        title: content.title,
        description: content.description,
        coverUrl: content.coverUrl ? content.coverUrl : "",
        messageCompleted: content.messageCompleted
          ? content.messageCompleted
          : "",
        percentCompleted: content.percentCompleted
          ? content.percentCompleted
          : 0,
        tags: content.tags ? content.tags : [],
        privacy: content.privacy ? content.privacy : false,
        status: content.status ? content.status : 0,
        newComment: content.newComment,
        showComments: content.showComments,
        duration: content.duration ? content.duration : 0,
        isRepeated: content.isRepeated ? content.isRepeated : true,
        startDate: content.startDate ? content.startDate : null,
        endDate: content.endDate ? content.endDate : null,

        updatedOn: updatedOn,
        updatedBy: updatedBy,
        author: this.userLogin,
        timestamp: new Date().getTime(),
        isReviewed: false,
      })
      .then((docRef) => {
        const syncData = { id: content.id, ...content };
        this.sync(syncData);

        return docRef;
      });
  }

  updateQuizzes(id, quizzes: ExerciseQuiz[]): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.contentRef.doc(id).update({
      exQuizzes: quizzes ? JSON.parse(JSON.stringify(quizzes)) : [],
      noQuiz: quizzes ? quizzes.length : 0,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      author: this.userLogin,
      timestamp: new Date().getTime(),
      isReviewed: false,
    });
  }

  updateUsersHighest(id, topUser: ExerciseUser[]) {
    let topUserSave: ExerciseUser[] = [];
    _.forEach(topUser, (e) => {
      const item = new ExerciseUser();
      item.id = e.id;
      item.author = e.author;
      item.createdBy = e.createdBy;
      item.percentCompleted = e.percentCompleted;
      item.duration = e.duration;
      item.percentage = e.percentage;
      item.timeSpent = e.timeSpent;
      item.createdOn = e.createdOn;

      // item.totalQuiz = e.totalQuiz;
      // item.noQuiz = e.noQuiz;
      // item.noCorrect = e.noCorrect;
      // item.noIncorrect = e.noIncorrect;
      // item.updatedOn = e.updatedOn;

      topUserSave.push(item);
    });
    topUserSave = topUserSave ? JSON.parse(JSON.stringify(topUserSave)) : [];
    // console.log("updateUsersHighest:", JSON.stringify(topUserSave));
    console.log("updateUsersHighest/id:", id);

    // using functions
    const callable = this.fns.httpsCallable("exercise-updateTopExUsers");
    return callable({
      id: id,
      exerciseUsers: JSON.stringify(topUserSave),
    });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.contentRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      timestamp: new Date().getTime(),
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.tags = JSON.stringify(content.tags);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Exercises", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
